import { rollups, sum, ascending, descending, max } from "d3-array";
import { scaleLinear } from "d3-scale";
export default function() {
  let directionLeftToRight;
  let sourceTotalHeight;
  let targetHeight;
  let padding;
  let sourceInnerRadius;
  let sourceOuterRadius;
  let targetInnerRadius;
  let targetOuterRadius;
  let sourcesOrder;

  function sankeyBar(flows, targetsSet) {
    flows = flows.filter(f => targetsSet.has(f.target));

    // SOURCES
    // Total value sum
    const total = sum(flows, d => d.value);

    // Source group sum
    const sourceGroups = rollups(
      flows,
      v => ({
        data: v.sort((a, b) => descending(a.value, b.value)),
        value: sum(v, d => d.value)
      }),
      d => d.source
    ).map(([key, value]) => ({ key, ...value }));

    if (sourcesOrder)
      sourceGroups.sort((a, b) =>
        ascending(sourcesOrder.indexOf(a.key), sourcesOrder.indexOf(b.key))
      );

    const sourceKeys = sourceGroups.map(d => d.key);

    // Convert the sum to scaling factor
    const ks =
      (sourceTotalHeight - (sourceGroups.length - 1) * padding) / total;
    // Calculate the layout of sources
    let a = -sourceTotalHeight / 2;
    sourceGroups.forEach((s, i) => {
      s.index = i;
      s.x0 = directionLeftToRight ? sourceInnerRadius : -sourceInnerRadius;
      s.x1 = directionLeftToRight ? sourceOuterRadius : -sourceOuterRadius;
      s.y0 = a;
      s.subGroupsYs = s.data.map(d => ({
        target: d.target,
        y0: a,
        y1: (a += d.value * ks)
      }));
      s.y1 = a;
      a += padding;
    });

    // TARGETS
    // Target group sum
    const targetGroups = rollups(
      flows,
      v => ({
        data: v.sort((a, b) =>
          descending(sourceKeys.indexOf(a.source), sourceKeys.indexOf(b.source))
        ),
        value: sum(v, d => d.value)
      }),
      d => d.target
    ).map(([key, value]) => ({ key, ...value }));

    // Sort target group by value
    targetGroups.sort((a, b) => descending(a.value, b.value));

    // Target group bandwidth angle and length
    const targetTotalHeight =
      targetGroups.length * targetHeight + (targetGroups.length - 1) * padding;
    // Target group bar scale
    const h = scaleLinear()
      .domain([0, max(targetGroups, t => t.value)])
      .range([targetOuterRadius, targetInnerRadius]);

    // Calculate the layout of targets
    a = -targetTotalHeight / 2;
    targetGroups.forEach((t, i) => {
      t.index = i;
      t.x0 = directionLeftToRight ? h(t.value) : -h(t.value);
      t.x1 = directionLeftToRight ? targetOuterRadius : -targetOuterRadius;
      t.y0 = a;
      t.y1 = a += targetHeight;
      a += padding;
      // Stack
      let b = 0;
      let stacked = [];
      rollups(
        t.data,
        v => sum(v, d => d.value),
        d => d.source
      ).forEach(([key, value]) => {
        const e = directionLeftToRight
          ? [h(b), h((b += value))]
          : [-h(b), -h((b += value))];
        e.key = key;
        e.bandwidth = targetHeight;
        stacked.push(e);
      });
      t.stacked = stacked;
    });

    // Links
    const sourceGroupsMap = new Map(sourceGroups.map(s => [s.key, s]));
    const targetGroupsMap = new Map(targetGroups.map(t => [t.key, t]));
    const links = flows.map(f => {
      const s = sourceGroupsMap.get(f.source);
      const t = targetGroupsMap.get(f.target);
      const sg = s.subGroupsYs.find(d => d.target === t.key);
      const source = {
        key: s.key,
        x: s.x1,
        y0: sg.y0,
        y1: sg.y1
      };
      const target = {
        key: t.key,
        x: t.x0,
        y0: t.y0,
        y1: t.y1
      };
      return {
        ...f,
        source,
        target
      };
    });

    return {
      sourceGroups,
      targetGroups,
      links
    };
  }

  sankeyBar.sourceTotalHeight = function(_) {
    return arguments.length
      ? ((sourceTotalHeight = _), sankeyBar)
      : sourceTotalHeight;
  };

  sankeyBar.targetHeight = function(_) {
    return arguments.length ? ((targetHeight = _), sankeyBar) : targetHeight;
  };

  sankeyBar.padding = function(_) {
    return arguments.length ? ((padding = _), sankeyBar) : padding;
  };

  sankeyBar.sourcesOrder = function(_) {
    return arguments.length ? ((sourcesOrder = _), sankeyBar) : sourcesOrder;
  };

  sankeyBar.directionLeftToRight = function(_) {
    return arguments.length
      ? ((directionLeftToRight = _), sankeyBar)
      : directionLeftToRight;
  };

  sankeyBar.sourceInnerRadius = function(_) {
    return arguments.length
      ? ((sourceInnerRadius = Math.max(0, _)), sankeyBar)
      : sourceInnerRadius;
  };

  sankeyBar.sourceOuterRadius = function(_) {
    return arguments.length
      ? ((sourceOuterRadius = Math.max(0, _)), sankeyBar)
      : sourceOuterRadius;
  };

  sankeyBar.targetInnerRadius = function(_) {
    return arguments.length
      ? ((targetInnerRadius = Math.max(1, _)), sankeyBar)
      : targetInnerRadius;
  };

  sankeyBar.targetOuterRadius = function(_) {
    return arguments.length
      ? ((targetOuterRadius = Math.max(1, _)), sankeyBar)
      : targetOuterRadius;
  };

  return sankeyBar;
}
